import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { Node } from "@iventis/domain-model/model/node";
import { NodeType } from "@iventis/domain-model/model/nodeType";
import { LoadingComponent, AppBarSubmenu, StyledAppBarContainer } from "@iventis/components";
import { removeQueryParam } from "@iventis/utilities";
import { TreeBrowserQueryString } from "@iventis/tree-browser";
import { useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "@iventis/toasts/src/toast";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { AppSubBarMenuContext } from "@iventis/utilities/src/app-bar-submenu-context";
import { IventisErrorBoundaryWithToast } from "@iventis/error-boundaries";
import { useHistory } from "@iventis/subscriptions";
import { sidebar } from "./state/sidebar.slice";
import MapBrowserComponent from "./plan-library/map-browser";
import { RootDispatch, RootState } from "./state/root.store";
import { createMappingLibraries, getStaticLibraries, AppBarSubMenuNode, staticLibraries, StaticLibrary, StaticLibraryId } from "./plan-library/mapping-library.helpers";
import { favouriteNodeServices, libraryNodeServices, recentlyViewedNodeServices } from "./services/library-api-functions";
import { getSpatialLibraries } from "./services/libraries";

const SpatialHomeComponent: FunctionComponent<PropsWithChildren<{
    match: { params: { libraryId?: string } };
}>> = ({ match, children }) => {
    const history = useHistory();
    const { pathname, search } = useLocation();

    const dispatch = useDispatch<RootDispatch>();

    const translate = useIventisTranslate();

    const { libraryId } = match.params;

    const { userId, subscriptionPlan } = useSelector((s: RootState) => ({ subscriptionPlan: s.auth.subscriptionPlan, userId: s.auth.user?.userId }));

    const [placeHolderLibraryDetails, setPlaceHolderLibraryDetails] = useState<AppBarSubMenuNode[]>(undefined);
    const [libraryNodes, setLibraryNodes] = useState<Node[]>(undefined);

    const { mobileDrawerOpen, setMobileDrawerOpen } = useContext(AppSubBarMenuContext);

    useEffect(() => {
        const abortController = new AbortController();
        getSpatialLibraries(abortController.signal).then((libraries: Node[]) => {
            if (libraries != null) {
                // Makes the mobile drawer open on first render for a logical user journey
                setMobileDrawerOpen(true);
                setPlaceHolderLibraryDetails(createMappingLibraries([...libraries, ...getStaticLibraries()], subscriptionPlan));
                setLibraryNodes([...libraries, ...getStaticLibraries()]);
                const personalMappingLibraries = libraries.filter(({ type }) => type === NodeType.PersonalMappingLibrary);
                const mappingLibraries = libraries.filter(({ type }) => type === NodeType.MappingLibrary);
                const libraryToSelect = libraryId ? [...libraries, ...getStaticLibraries()].find((n) => n.id === libraryId) : personalMappingLibraries[0] ?? mappingLibraries[0];
                onLibraryChange(libraryToSelect);
            }
        });
        return () => abortController.abort();
    }, []);

    const onLibraryChange = (selectedLibrary: Node, clearUrl?: boolean, newMainNodeId?: string) => {
        if (selectedLibrary.id !== libraryId) {
            dispatch(sidebar.actions.setPreviousLibraryId(selectedLibrary.id));
            const staticLibraries = getStaticLibraries();
            let newSearch: string;
            if (clearUrl || staticLibraries.some((library) => library.id === selectedLibrary.id)) {
                newSearch = removeQueryParam(search, TreeBrowserQueryString.MAIN_NODE_ID);
                newSearch = removeQueryParam(newSearch, TreeBrowserQueryString.EXPANDED_NODE_ID);
            } else if (newMainNodeId) {
                newSearch = `?${TreeBrowserQueryString.MAIN_NODE_ID}=${newMainNodeId}`;
            } else {
                newSearch = search;
            }
            history.push({ ...history.location, pathname: `/spatial-planner/${selectedLibrary.id}`, search: newSearch });
        }
    };

    const selectedLibraryNode = useMemo(() => libraryNodes?.find((lib) => lib.id === pathname?.split("/spatial-planner/")[1]?.split("/")[0]), [pathname, libraryNodes]);

    // Get the current StaticLibrary value if it matches the one in the enum
    const staticLibrary: StaticLibrary = useMemo(() => staticLibraries.find((staticLib) => staticLib.id === selectedLibraryNode?.id), [selectedLibraryNode?.id]);

    const allowTreeActions = staticLibrary === undefined;

    const nodeService = useMemo(() => {
        if (staticLibrary != null) {
            switch (staticLibrary.id) {
                case StaticLibraryId.RecentlyViewed:
                    return recentlyViewedNodeServices;
                case StaticLibraryId.Favourites:
                    return favouriteNodeServices;
                default:
                    return libraryNodeServices;
            }
        } else {
            return {
                ...libraryNodeServices,
                moveNodes: (nodes, tree) =>
                    libraryNodeServices.moveNodes(nodes, tree).then(() =>
                        toast.success({
                            title: translate(Content.common.success),
                            message: translate(Content.library2.move_node.success),
                        })
                    ),
            };
        }
    }, [staticLibrary]);

    return (
        <StyledAppBarContainer className="spatial-page">
            {placeHolderLibraryDetails ? (
                <>
                    <AppBarSubmenu<AppBarSubMenuNode>
                        items={placeHolderLibraryDetails}
                        selectedItem={libraryId}
                        onItemClick={(libraryId) => {
                            onLibraryChange(libraryId, true);
                            if (mobileDrawerOpen) setMobileDrawerOpen(false);
                        }}
                        fullScreenTitle="Maps"
                        mobileOpen={mobileDrawerOpen}
                        translationObject={Content.library2.mappingLibraries}
                    />
                    <IventisErrorBoundaryWithToast toast={toast}>
                        {libraryNodes.length > 0 && selectedLibraryNode && (
                            <MapBrowserComponent
                                allowTreeActions={allowTreeActions}
                                libraries={placeHolderLibraryDetails}
                                selectedLibraryNode={selectedLibraryNode}
                                nodeServices={nodeService}
                                userId={userId}
                                staticLibrary={staticLibrary}
                                onLibraryChange={(tree, mainNodeId) => onLibraryChange(tree, false, mainNodeId)}
                            />
                        )}
                    </IventisErrorBoundaryWithToast>
                </>
            ) : (
                <LoadingComponent />
            )}
            {children}
        </StyledAppBarContainer>
    );
};

export default SpatialHomeComponent;
